import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import BookingQrCode from "../Common/BookingQrCode";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import moment from "moment";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

const BookingConfirmation = () => {
  useDocumentTitle("Booking Confirmation");
  const navigate = useNavigate();
  const { endpoint } = useSelector((state) => state.auth);

  const { state: pageState } = useLocation();
  const hasBookingData = Boolean(pageState?.bookingId);

  const {
    guests = 0,
    bookingId = "",
    firstName = "",
    lastName = "",
    phone = "",
    entryFee = 0,
    coverCharge = 0,
    currencySymbol = "",
    hideBackToHome = false,
    bookingDateTime = null,
    storeId = null,
    customerId = "",
  } = pageState || {};

  const bookingForQr = useMemo(
    () => ({
      id: bookingId,
      booking_datetime: bookingDateTime || "",
      store: { id: storeId },
    }),
    [bookingId, bookingDateTime, storeId]
  );

  const fullName = useMemo(() => {
    const parts = [firstName, lastName].filter(Boolean);
    return parts.join(" ");
  }, [firstName, lastName]);

  const totalAmount = useMemo(() => {
    const entry = (entryFee || 0) * (guests || 0);
    const cover = coverCharge || 0;
    return entry + cover;
  }, [entryFee, coverCharge, guests]);

  const handleBackToHome = () => {
    navigate("/");
  };

  const isCoverBooking = useMemo(() => {
    return entryFee > 0 || coverCharge > 0;
  }, [entryFee, coverCharge]);

  const shouldShowQR = useMemo(() => {
    if (!bookingId) return false;
    if (!bookingDateTime) return true;

    const bookingMoment = moment(bookingDateTime);
    const currentMoment = moment();

    if (currentMoment.isBefore(bookingMoment)) {
      return true;
    }

    const hoursDiff = currentMoment.diff(bookingMoment, "hours", true);
    return hoursDiff <= 4;
  }, [bookingDateTime, bookingId]);

  if (!hasBookingData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
          <ErrorOutlineIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Booking details unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This page is only available immediately after completing a booking. If you
            refreshed or opened this link directly, your confirmation details are no
            longer available here.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 4 }}
            onClick={() => navigate("/")}
          >
            Back to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 80, color: "green", mb: 2 }} />
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Thank you for your booking!
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          We’ve received your reservation for <strong>{guests}</strong> guests.
        </Typography>
        {isCoverBooking && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            To confirm booking, now please pay on the gate. Ask staff for any assistance.
          </Typography>
        )}
        {shouldShowQR ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Box sx={{ borderRadius: 1, overflow: "hidden" }}>
                <BookingQrCode
                  booking={bookingForQr}
                  customerId={customerId}
                  appConfigName={endpoint}
                  size={200}
                  style={{ width: "200px" }}
                />
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Booking ID: <strong>#{bookingId}</strong>
            </Typography>
          </>
        ) : (
          <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              QR Code is no longer available.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Booking ID: <strong>#{bookingId}</strong>
            </Typography>
          </Box>
        )}
        <Typography variant="body2" sx={{ mt: 1 }}>
          Name: <strong>{fullName || "-"}</strong>
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Phone: <strong>{phone || "-"}</strong>
        </Typography>

        {isCoverBooking && (
          <Box sx={{ mt: 3, textAlign: "left" }}>
            {/* <Typography variant="subtitle1" fontWeight="bold">Calculation</Typography> */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
              <Box>
                <Typography variant="body2">{`Entry Fee: ${currencySymbol}${entryFee || 0}`}</Typography>
                <Typography variant="caption" color="text.secondary">{`(${guests} Guest X ${currencySymbol}${entryFee || 0})`}</Typography>
              </Box>
              <Box sx={{ alignItems: "flex-start" }}>
                <Typography variant="body2">{`${currencySymbol}${(entryFee || 0) * (guests || 0)}`}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
              <Typography variant="body2">Cover Charge</Typography>
              <Typography variant="body2">{`${currencySymbol}${coverCharge || 0}`}</Typography>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="subtitle1" fontWeight="bold">Total Amount</Typography>
              <Typography variant="subtitle1" fontWeight="bold">{`${currencySymbol}${totalAmount}`}</Typography>
            </Box>
          </Box>
        )}
        {hideBackToHome ? null : (
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 4 }}
            onClick={handleBackToHome}
          >
            Back to Home
          </Button>
        )}
      </Paper>
    </Container>
  );
};

export default BookingConfirmation;
